import moment from 'moment'
// import momentDurationFormatSetup from 'moment-duration-format'
import { capitalize, formatDuration, Sex } from 'utilities'
import { Order, OrderInterface } from './Order'
import { observable, action } from 'mobx'
import { ViewerEventEnums, ViewerEvent, ViewerEventInterface } from './ViewerEvent'
import { SnapshotDataEntity, SnapshotDataDTO } from './SnapshotDataEntity'

// momentDurationFormatSetup(moment)
export interface Address {
  id: number
  country: string
  city: string
  address: string
  zipcode: string
}
export interface AddressDTO {
  id: number
  name: string
  primary: boolean
  billingAddress: Address
  shippingAddress: Address
}

export interface ViewerInterface {
  id: number
  firstName: string
  lastName: string
  emailAddress: string
  sex: Sex
  dateOfBirth: string
  imageUrl: string
  addresses: AddressDTO[]
  totalWatchTime: number
  totalInteractionTime: number
  totalAddRevenue: number
  totalSpent: number
  //   changePasswordCode: "YzcxYmRmNzktZjY4OS00ODQyLWE0NDktZDg2ZTkyNTk4OWIx"
  //   externalId: "U9"
  //   marketplaceId: 135
  //   organizations: Proxy { 0: Proxy, Symbol(mobx administration): ObservableArrayAdministration }
  // phoneNumber: null
  // roles: Proxy { 0: Proxy, Symbol(mobx administration): ObservableArrayAdministration }
  // shippingAddress: null
  // shippingCity: null
  // shippingCountry: null
  // shippingZipcode: null
  lastTimeStamp : string
}

export interface ViewerOrderStatistics {
  userId: number
  totalSpent: number
}

export interface ViewerStatistics {
  userId: string
  totalWatchTime: number
  totalInteractionTime: number
  totalAddRevenue: number
  totalSpent?: number
}

type Events = {
  [ViewerEventEnums.VIDEO_STOPPED]: ViewerEvent[]
  [ViewerEventEnums.INTERACTION_STOPPED]: ViewerEvent[]
}

export class Viewer {
  readonly id: number;
  readonly firstName: string;
  readonly lastName: string;
  readonly emailAddress: string;
  readonly sex: Sex;
  readonly dateOfBirth: Date
  readonly imageUrl: string
  readonly address: string
  readonly city: string
  readonly country: string
  readonly zip: string
  totalWatchTime: number
  totalInteractionTime: number
  totalAddRevenue: number
  totalSpent: number
  lastTimeStamp: string
  @observable snapshotData: SnapshotDataEntity[] = []
  @observable orders: Order[] = []
  @observable events: Events = {
    [ViewerEventEnums.VIDEO_STOPPED]: [],
    [ViewerEventEnums.INTERACTION_STOPPED]: []
  }

  constructor(viewer: ViewerInterface) {
    this.id = viewer.id
    this.firstName = viewer.firstName
    this.lastName = viewer.lastName
    this.emailAddress = viewer.emailAddress
    this.sex = viewer.sex
    this.dateOfBirth = new Date(viewer.dateOfBirth)
    this.imageUrl = viewer.imageUrl

    const address = (viewer.addresses && viewer.addresses.length)
      ? viewer.addresses.find(a => a.primary)
      : null

    this.address = address ? address.billingAddress.address : ''
    this.city = address ? address.billingAddress.city : ''
    this.country = address ? address.billingAddress.country : ''
    this.zip = address ? address.billingAddress.zipcode : ''
    this.totalWatchTime = viewer.totalWatchTime || 0
    this.totalInteractionTime = viewer.totalInteractionTime || 0
    this.totalAddRevenue = viewer.totalAddRevenue || 0
    this.totalSpent = viewer.totalSpent || 0
    this.lastTimeStamp = viewer.lastTimeStamp
  }

  @action.bound setOrders(orders: OrderInterface[]) {
    this.orders = orders.map(order => new Order(order))
  }

  @action.bound serViewerEvents(events: ViewerEventInterface[], eventType: ViewerEventEnums) {
    this.events[eventType] = events.map(ev => new ViewerEvent(ev))
  }

  @action.bound setStatistics(stats: ViewerStatistics) {
    this.totalWatchTime = stats.totalWatchTime
    this.totalInteractionTime = stats.totalInteractionTime
    this.totalAddRevenue = stats.totalAddRevenue
    this.totalSpent = stats.totalSpent || 0
  }

  @action.bound setSnapshotData(entities: SnapshotDataDTO[]) {
    this.snapshotData = entities.map(entity => new SnapshotDataEntity(entity))
  }

  get fullName() {
    return `${this.firstName} ${this.lastName}`
  }

  get birthDate() {
    return moment(this.dateOfBirth).format('DD MMM YYYY')
  }

  get age() {
    return moment().diff(this.dateOfBirth, 'years')
  }

  get fullAddress() {
    const address = this.address
      ? `${this.address}, `
      : ''
    const city = this.city
      ? `${this.city}, `
      : ''
    const zip = this.zip
      ? `${this.zip} `
      : ''
    const country = this.country || ''
    return capitalize(`${address}${zip}${city}${country}`, true)
  }

  get gender() {
    return capitalize(this.sex || '', true)
  }

  get totalWatchTimeAsString() {
    return formatDuration(this.totalWatchTime)
  }

  get totalInteractionTimeAsString() {
    return formatDuration(this.totalInteractionTime)
  }

  get totalAddRevenueAsString() {
    return `$${this.totalAddRevenue.toFixed(2)}`
  }

  get totalSpentAsString() {
    return `$${this.totalSpent.toFixed(2)}`
  }

  areEventsLoaded = (type: ViewerEventEnums) => {
    return (this.events[type] || []).length > 0
  }

  areOrdersLoaded = () => {
    return (this.orders || []).length > 0
  }

  isSnapshotDataLoaded = () => {
    return (this.snapshotData || []).length > 0
  }
}
